import type { components } from '@/lib/api/types/openapi';

/**
 * トランジションスカウト（業務委託から正社員転換を検討する前提のスカウト）を企業に見せる・入力させるための整形。
 *
 * フィット表示は「一致 / 不一致」の判定をしない（判定ロジックは未定のため）。企業の提示と候補者の希望を並べるだけにする。
 * 企業の入力項目に無いもの（転換後の働き方・副業）は「比較対象なし」と出す。
 * 設計は `docs/spec/issue-1050/README.md`。
 */

export type TransitionScout = NonNullable<components['schemas']['TransitionScout']>;
export type TransitionScoutResponse = NonNullable<TransitionScout['talentResponse']>;
export type CreateTransitionScoutInput = components['schemas']['CreateTransitionScoutInput'];
/** 企業向け候補者詳細の正社員転換の希望条件。意向が前向きでない候補者は null で届く。 */
export type TalentFullTimePreference = NonNullable<components['schemas']['Talent']['fullTimePreference']>;

export const TRANSITION_RESPONSE_LABEL: Record<TransitionScoutResponse, string> = {
  INTERESTED: '興味がある',
  CONTRACT_ONLY: '業務委託のみなら興味がある',
  DECLINED: '見送る',
};

/** 送信時（企業）・回答時（候補者）に出す、トランジションスカウトの趣旨（Issue #1050 の文面）。 */
export const TRANSITION_SCOUT_NOTICE = [
  '正社員トランジションスカウトは、一定期間の業務委託参画後に双方の意向を確認し、正社員転換を検討する仕組みです。',
  '想定期間の終了前後に、必要に応じてカスタマーサクセス担当者より意向確認の面談をご案内する場合があります。',
] as const;

export const ANNUAL_SALARY_DISCLAIMER = '想定年収は正社員転換時の条件を保証するものではありません（候補者にもそう表示します）。';

/** 意向確認の目安のプリセット。「その他」は月数を入力する。 */
export const INTENT_CHECK_PRESETS = [
  { value: '3', label: '3ヶ月' },
  { value: '6', label: '6ヶ月' },
  { value: 'other', label: 'その他' },
] as const;
export type IntentCheckPreset = (typeof INTENT_CHECK_PRESETS)[number]['value'];

export const YEN_PER_MAN = 10_000;
/** 年収の刻み（万円）。サーバと同じ10万円刻み。 */
export const ANNUAL_SALARY_STEP_MAN = 10;
/** 年収の上限（万円）。サーバと同じ。10万円刻みに合わせて 99,990万円。 */
export const ANNUAL_SALARY_MAX_MAN = 99_990;
export const INTENT_CHECK_MONTHS_MAX = 36;
export const TRANSITION_NOTE_MAX_LENGTH = 200;

const NOT_ANSWERED = '未回答';
/** 候補者が希望条件を出していない（未登録、または正社員転換の意向が前向きでない）。 */
const NOT_AVAILABLE = '確認できません';
/** 企業の提示に対応する項目が無いもの。 */
export const NOT_COMPARABLE = '比較対象なし';

export const formatManYenRange = (min: number, max: number): string => {
  const toMan = (yen: number) => (yen / YEN_PER_MAN).toLocaleString();
  return min === max ? `${toMan(min)}万円` : `${toMan(min)}万円〜${toMan(max)}万円`;
};

export const formatIntentCheck = (months: number | null): string =>
  months == null ? '未設定' : `業務委託の開始から${months}か月後`;

const WORKSTYLE_LABEL: Record<NonNullable<TalentFullTimePreference['workstyle']>, string> = {
  FULL_TIME_REMOTE: 'フルリモート',
  PART_TIME_REMOTE: '一部出社可能',
  FLEXIBLE: 'どちらでも良い',
};

/** フィット表示に使う、企業の提示条件。入力中のフォームからも、送信済みのスカウトからも作れる形。 */
export type TransitionOffer = {
  annualSalaryMin: number | null;
  annualSalaryMax: number | null;
  positionNames: string[];
};

export type TransitionFitRow = {
  label: string;
  /** 企業の提示。企業の入力項目に無いものは null（比較対象なし）。 */
  offer: string | null;
  /** 候補者の希望。 */
  preference: string;
};

/** 企業の提示と候補者の希望を並べた行。判定はしない。 */
export const buildTransitionFitRows = (
  offer: TransitionOffer,
  preference: TalentFullTimePreference | null | undefined,
): TransitionFitRow[] => {
  const preferenceOr = (value: string | null | undefined) => (preference ? (value ?? NOT_ANSWERED) : NOT_AVAILABLE);
  const offerSalary =
    offer.annualSalaryMin != null && offer.annualSalaryMax != null
      ? formatManYenRange(offer.annualSalaryMin, offer.annualSalaryMax)
      : '未入力';

  return [
    {
      label: '年収',
      offer: offerSalary,
      preference: preferenceOr(preference ? formatManYenRange(preference.annualSalaryMin, preference.annualSalaryMax) : null),
    },
    {
      label: '職種・ポジション',
      offer: offer.positionNames.join('、') || '未入力',
      preference: preferenceOr(preference?.positions.map((position) => position.name).join('、') || null),
    },
    {
      label: '働き方',
      offer: null,
      preference: preferenceOr(preference?.workstyle ? WORKSTYLE_LABEL[preference.workstyle] : null),
    },
    {
      label: '副業',
      offer: null,
      preference: preferenceOr(preference?.wantsSideJob == null ? null : preference.wantsSideJob ? '続けたい' : '続けない'),
    },
  ];
};

/** 送信済みのスカウトから、フィット表示用の企業の提示条件を作る。 */
export const toTransitionOffer = (transition: TransitionScout): TransitionOffer => ({
  annualSalaryMin: transition.annualSalaryMin,
  annualSalaryMax: transition.annualSalaryMax,
  positionNames: transition.positions.map((position) => position.name),
});

/**
 * REST の候補者には `fullTimePreference` があるが、画面は GraphQL の型で受け取っている。REST にしか無い項目をここで読む。
 * 候補者一覧から開いた場合は項目自体が無い（一覧では返さない）ので undefined になる。
 */
export const readTalentFullTimePreference = (talent: unknown): TalentFullTimePreference | null | undefined => {
  if (!talent || typeof talent !== 'object' || !('fullTimePreference' in talent)) return undefined;
  return (talent as { fullTimePreference?: TalentFullTimePreference | null }).fullTimePreference ?? null;
};

/**
 * 案件のポジション名。REST の案件一覧には `projectMasterEngineerPositions` があるが、画面は GraphQL の型で受け取っているので、ここで読む。
 * 正社員トランジションスカウトの想定ポジションとしてそのまま使う（送信時にサーバが同じものを写し取る）。
 */
export const readProjectPositionNames = (project: unknown): string[] =>
  ((project as { projectMasterEngineerPositions?: { name: string }[] | null } | undefined)?.projectMasterEngineerPositions ?? []).map(
    (position) => position.name,
  );
